import { useState } from "react";
import useAxios from "../utils/useAxios";

// Flattens every row bucket (created/duplicate/updated/error) back into one
// array of plain row payloads (sorted by row number), optionally overriding
// one field on one row and/or dropping one row entirely - the single source
// of truth for what gets resent to <domain>/import-rows/, since every
// bucket already carries the full per-row field snapshot the backend echoes
// back. "updated" rows are included too so a row the backend already
// auto-detected as differing from an existing asset stays in the resend and
// keeps being re-compared, not silently dropped back to a plain duplicate.
const buildRowsPayload = (
  { createdRows, duplicateRows, updatedRows, rowErrors },
  rowFields,
  overrideRowNumber,
  overrideField,
  overrideValue,
  excludeRowNumber,
) => {
  const flatRows = [...createdRows, ...duplicateRows, ...(updatedRows || []), ...rowErrors];

  return flatRows
    .filter((row) => row.row !== excludeRowNumber)
    .slice()
    .sort((a, b) => a.row - b.row)
    .map((row) => {
      const base = { row: row.row };
      rowFields.forEach((field) => {
        base[field] = row[field];
      });
      if (row.row === overrideRowNumber) {
        base[overrideField] = overrideValue;
      }
      return base;
    });
};

/**
 * Stateful orchestration for a Tier-style bulk Excel upload: pick a file,
 * auto-preview it (dry-run), let the caller fix/remove individual rows with
 * live re-validation, then submit for real - all against a pair of
 * endpoints shaped like FrequencyShift.assets' <domain>_views.py
 * (import-excel/, import-rows/, import-template/). Extracted out of
 * TierCsvUpload.jsx so Equipment/Instrument uploads configure it instead of
 * re-implementing the same state machine.
 *
 * `extraContext` is merged into every import-excel/import-rows request
 * (e.g. {plant_id} for Tier/Equipment, {site_id, plant_id, equipment_id,
 * tier_id, component_id} for Instrument) and passed as-is as the
 * import-template GET's query params.
 */
const useBulkImportPreview = ({
  importExcelUrl,
  importRowsUrl,
  templateUrl,
  templateFilename,
  rowFields,
  extraContext = {},
}) => {
  const api = useAxios();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const createdRows = preview?.created_rows ?? [];
  const duplicateRows = preview?.duplicate_rows ?? [];
  const updatedRows = preview?.updated_rows ?? [];
  const rowErrors = preview?.row_errors ?? [];

  const revalidateRows = async (rows) => {
    setRevalidating(true);
    setError("");
    try {
      const response = await api.post(
        importRowsUrl,
        { rows, ...extraContext, dry_run: true },
        { headers: { "Content-Type": "application/json" } },
      );
      setPreview(response.data);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Could not re-check the file.";
      setError(message);
    } finally {
      setRevalidating(false);
    }
  };

  const handleRowFieldEdit = (rowNumber, field, value) => {
    const rows = buildRowsPayload(
      { createdRows, duplicateRows, updatedRows, rowErrors },
      rowFields,
      rowNumber,
      field,
      value,
    );
    revalidateRows(rows);
  };

  const handleRemoveRow = (rowNumber) => {
    const rows = buildRowsPayload(
      { createdRows, duplicateRows, updatedRows, rowErrors },
      rowFields,
      undefined,
      undefined,
      undefined,
      rowNumber,
    );

    if (rows.length === 0) {
      setPreview(null);
      setFile(null);
      return;
    }

    revalidateRows(rows);
  };

  const runPreview = async (selectedFile) => {
    try {
      setPreviewing(true);
      setError("");

      const formData = new FormData();
      formData.append("file", selectedFile);
      Object.entries(extraContext).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      });
      formData.append("dry_run", true);

      const response = await api.post(importExcelUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreview(response.data);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Could not read the file.";

      setError(message);
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError("");
    setPreview(null);

    if (selectedFile) {
      runPreview(selectedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      setError("");

      const response = await api.get(templateUrl, {
        params: extraContext,
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = templateFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { ok: true };
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to download the template.";
      setError(message);
      return { ok: false, message };
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleSubmit = async () => {
    if (!file || !preview) {
      const message = "Please choose a file and wait for the preview to finish.";
      setError(message);
      return { ok: false, message };
    }

    try {
      setUploading(true);
      setError("");

      const rows = buildRowsPayload({ createdRows, duplicateRows, updatedRows, rowErrors }, rowFields);

      const response = await api.post(
        importRowsUrl,
        { rows, ...extraContext, dry_run: false },
        { headers: { "Content-Type": "application/json" } },
      );

      setFile(null);
      setPreview(null);

      return { ok: true, data: response.data };
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Upload failed.";
      setError(message);
      return { ok: false, message };
    } finally {
      setUploading(false);
    }
  };

  return {
    file,
    preview,
    previewing,
    revalidating,
    uploading,
    downloadingTemplate,
    error,
    rowErrors,
    handleFileChange,
    handleDownloadTemplate,
    handleRowFieldEdit,
    handleRemoveRow,
    handleSubmit,
  };
};

export default useBulkImportPreview;
